import ReactUseWebSocket from 'react-use-websocket';
import { MHR_FEATURE, POSE_BACKEND } from '../../../../config/features';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { rectangleFlag, STAGE_STATUS } from '../../../constants/constants';
import 'twin.macro';
import {
  addTimer,
  deleteTimer,
  removeFromLocalStorage,
  removeFromLocalStorageInPerformance,
} from '../../../utilis/helpers';
import { SOCKET_URL } from '../../../constants/domain';
import { reRegisterRealtimeCall } from '../../../services/realtime-connection-store';
import { normalizePauseStageItems } from '../../../utilis/exercise-status';
import { cleanupStream, resolveFrameGeometry, SESSION_WALL_TIMER_KEY } from '../helpers';
import { useNavigate } from 'react-router';
import { getSessionTokenFromUrl } from '../../../utilis/session-token';
import { VOICE_COMPANION_MODE, isVoiceCompanionMode } from '@/constants/voiceCompanion';
// Temporary developer-only observability (network connectivity investigation,
// Step 3). A no-op unless FEATURES.NETWORK_DIAGNOSTICS — see the store for the full rationale.
import { useNetworkDiagnostics } from '../../../zustand/network-diagnostics-store';
// Development-only network meter: attributes socket traffic to the current stage.
import { reportNetworkMeterStage } from '../../../services/network-meter';
import { onSocketMessage } from '../../../workers/pose-latency-probe';

const LATENCY_WINDOW_MS = 0;
const HOLD_TELEMETRY_MAX_DEPTH = 5;
const HOLD_TIMER_PAUSED_KEY = 'holdTimerPaused';
// How long the socket waits for the camera to report its resolution before it
// opens anyway, and how often it re-reads it while waiting. See the geometry gate
// below for why the wait exists at all.
const FRAME_GEOMETRY_WAIT_MS = 4000;
const FRAME_GEOMETRY_POLL_MS = 100;
// Upper bound on how long the socket is held closed while a lost camera is
// being reacquired. The config message is where `cameraAvailable` is reported,
// and the backend builds the whole session's evaluator from it and holds it for
// the socket's lifetime, so a socket that opens mid-reacquire runs the session
// as NO_MOTION_CAPTURE. Waiting is cheaper than the cure (forceReconnect
// below), which costs the in-flight set. Bounded, because a camera that is
// genuinely gone — permission revoked, no device — must still get a session:
// after this the socket opens and honestly reports no camera.
const CAMERA_REACQUIRE_HOLD_MS = 20000;
// Reconnect backoff. This used to reopen on a setTimeout(…, 0) with no backoff
// and no visibility check, which turned one dropped socket into a reconnect
// storm (every new connection claims the session epoch and supersedes the last)
// and — worse — reopened while the tab was still hidden and the camera still
// dead, which is exactly when `cameraAvailable` cannot be reported honestly.
const RECONNECT_BASE_DELAY_MS = 500;
const RECONNECT_MAX_DELAY_MS = 10000;
const RECONNECT_MAX_BACKOFF_STEPS = 5;
// A deliberate reopen (forceReconnect) waits this long between the close and the
// new socket, rather than the backoff a failure gets: nothing is wrong, the
// session just needs its config message sent again with a value that changed.
const FORCED_REOPEN_DELAY_MS = 300;

// Depth-first search for the block carrying a `hold` object. Landmark arrays are
// the only big payloads in a frame and they hold plain numbers, so bailing out of
// arrays of non-objects keeps this from walking thousands of entries per message.
function findHoldTelemetry(node, depth = 0) {
  if (node === null || typeof node !== 'object' || depth > HOLD_TELEMETRY_MAX_DEPTH) {
    return null;
  }

  if (Array.isArray(node)) {
    for (const item of node) {
      if (item === null || typeof item !== 'object') {
        return null;
      }
      const found = findHoldTelemetry(item, depth + 1);
      if (found) {
        return found;
      }
    }
    return null;
  }

  if (node.hold !== null && typeof node.hold === 'object' && !Array.isArray(node.hold)) {
    return node;
  }

  for (const value of Object.values(node)) {
    const found = findHoldTelemetry(value, depth + 1);
    if (found) {
      return found;
    }
  }

  return null;
}
// What the matcher's `conditionsState` block reports for `holdTimerPaused`. The
// matcher owns that shape (the guide calls it debug data), so this is a bounded
// search rather than a fixed path, and any `true` found anywhere in the block
// wins over a `false` beside it. Same bounded walk/array bail-out as
// findHoldTelemetry above, for the same reason: landmark arrays are the only
// large payloads and hold plain numbers.
//
// Returns true / false when the key is in the block, undefined when it isn't —
// the caller needs the three cases apart, since "the matcher said running" and
// "the matcher said nothing" are not the same thing.
function findHoldTimerPaused(node, depth = 0) {
  if (node === null || typeof node !== 'object' || depth > HOLD_TELEMETRY_MAX_DEPTH) {
    return undefined;
  }

  let sawRunning;

  if (Array.isArray(node)) {
    for (const item of node) {
      if (item === null || typeof item !== 'object') {
        return sawRunning;
      }
      const found = findHoldTimerPaused(item, depth + 1);
      if (found === true) {
        return true;
      }
      if (found === false) {
        sawRunning = false;
      }
    }
    return sawRunning;
  }

  if (typeof node[HOLD_TIMER_PAUSED_KEY] === 'boolean') {
    if (node[HOLD_TIMER_PAUSED_KEY]) {
      return true;
    }
    sawRunning = false;
  }

  for (const value of Object.values(node)) {
    const found = findHoldTimerPaused(value, depth + 1);
    if (found === true) {
      return true;
    }
    if (found === false) {
      sawRunning = false;
    }
  }

  return sawRunning;
}

// `holdTimerPaused` arrives in two places in the same frame:
//
//   data.holdTimerPaused        the frame's own flag — part of the out-of-frame
//                               warning trio (docs 04-frontend-guide §5.3).
//   conditionsState…hold        the matcher's diagnostics copy, at a path the
//                               matcher owns, hence the bounded search above.
//
// The clock stops only while BOTH sources report `true` on the same frame.
// Every other combination runs, including disagreement or a missing key.
//
// NO_MOTION_CAPTURE is unaffected: the page only consults this online, and only
// on TIMED_HOLD or TIMED_REPS — see the freeze flags in exercise/index.jsx.
//
// Exported for the unit test: the two sources agreeing is the whole contract, so
// it is pinned rather than described.
export function resolveHoldTimerRunning(message) {
  const data = message?.data ?? message?.response_data ?? message;
  const outerPaused = data?.[HOLD_TIMER_PAUSED_KEY] === true;
  const conditionsPaused = findHoldTimerPaused(data?.conditionsState) === true;
  return !(outerPaused && conditionsPaused);
}

// Timer-based sets pause only when both backend sources explicitly agree on
// `true`. Every other combination keeps the open timer running.
export function resolveTimedRepsTimerRunning(message) {
  return resolveHoldTimerRunning(message);
}

// `pauseStage` (the reply to the client's `pause` command) isn't guaranteed to
// live under `data` the way per-frame evaluation fields do — the backend is
// inconsistent about which top-level key wraps a given field (see the
// `latestLatency`/`currentFps`/`trigger_dispatched` reads below, which already
// deal with the same root vs `data` vs `response_data` split). Checked in that
// order and stops at whichever object actually carries the key, so an empty
// array sent at the root isn't shadowed by `data` having no such key at all.
function resolvePauseStageContainer(message) {
  const candidates = [message, message?.data, message?.response_data];
  return (
    candidates.find(
      candidate =>
        candidate &&
        typeof candidate === 'object' &&
        Object.prototype.hasOwnProperty.call(candidate, 'pauseStage')
    ) ?? null
  );
}

const useWebSocket = ReactUseWebSocket.default ?? ReactUseWebSocket;
const { ReadyState } = ReactUseWebSocket;

const UseWebSocketConnection = ({
  initialConfig,
  time,
  // Whether a live media source is actually feeding detection. `execution_mode`
  // says what the exercise was prescribed for; only the client knows whether a
  // camera is really behind it, so the two can disagree and the backend is told.
  cameraAvailable = false,
  enabled = true,
  videoRef = null,
  // Sourced from the URL (?voiceCompanionMode=…), carried there from the
  // prescription builder through /camera-permission and /try-demo. Takes
  // priority over the localStorage copy below when it's a valid mode.
  voiceCompanionMode = null,
  // True while a lost camera stream is being reacquired (see the reducer field
  // of the same name). Holds the socket closed for up to
  // CAMERA_REACQUIRE_HOLD_MS rather than letting it open — or reopen — with a
  // `cameraAvailable: false` that only reflects an in-flight reacquire.
  cameraReacquiring = false,
}) => {
  const [reConnect, setReconnect] = useState(true);
  // Stage 5 (POST_PERFORMANCE) is the backend ending the session itself: it
  // reports that stage and then closes the socket. That close is a normal end,
  // not a drop, so it must not go through the reconnect path in onClose —
  // otherwise the page immediately opens a fresh socket (and a fresh backend
  // session) for an exercise that is already over. Kept in state so the open
  // gate below re-evaluates, and mirrored into a ref because onClose reads a
  // closure created before the stage ever arrived.
  const [isSessionComplete, setIsSessionComplete] = useState(false);
  const isSessionCompleteRef = useRef(false);
  const [currentLatency, setCurrentLatency] = useState(null);
  const latencySamplesRef = useRef([]);
  const navigate = useNavigate();
  // Diagnostics only — see network-diagnostics-store. Declared early: it's a
  // dependency of scheduleReconnect's useCallback below, which needs it
  // available at the point that dependency array is evaluated (not merely by
  // the time scheduleReconnect is eventually called).
  const recordNetworkMetric = useNetworkDiagnostics(state => state.record);
  // `onOpen` is created before the camera reports in (handleUserMedia fires after
  // the socket is wired), so the closure value is stale by definition — read the
  // live one through a ref instead.
  const cameraAvailableRef = useRef(cameraAvailable);
  cameraAvailableRef.current = cameraAvailable;

  // The config message is the only place frame geometry can reach the backend as
  // session state — the protocol has no re-config message — so a config that goes
  // out before the <video> has painted (or before the track reports its settings)
  // leaves the backend on its 16:9 aspect and 1280x720 pixel defaults for the
  // whole session. `enabled` only tracks the pose model, which says nothing about
  // the camera, so hold the socket closed until the geometry is actually readable
  // and the connect payload carries the same width/height every landmark frame
  // carries. Bounded by FRAME_GEOMETRY_WAIT_MS: a source that never reports its
  // resolution must still start the session, defaults and all.
  const [isFrameGeometrySettled, setIsFrameGeometrySettled] = useState(false);
  // Bumped on every socket open, so callers can tell "still the same session
  // socket" from "this is a new one, and whatever was only ever sent on the old
  // one has to be sent again" — the pause command, above all (see the resync
  // effect in exercise/index.jsx).
  const [socketOpenCount, setSocketOpenCount] = useState(0);
  // The camera hold is deliberately not indefinite — see
  // CAMERA_REACQUIRE_HOLD_MS. Once expired, the gate below ignores
  // `cameraReacquiring` until it goes false and true again.
  const [isCameraHoldExpired, setIsCameraHoldExpired] = useState(false);

  useEffect(() => {
    if (!cameraReacquiring) {
      setIsCameraHoldExpired(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      console.warn(
        '[socket-config] camera reacquire did not settle in time; opening the session with the camera reported as unavailable.'
      );
      setIsCameraHoldExpired(true);
    }, CAMERA_REACQUIRE_HOLD_MS);

    return () => window.clearTimeout(timeoutId);
  }, [cameraReacquiring]);

  const isCameraHoldActive = cameraReacquiring && !isCameraHoldExpired;

  // Reconnect bookkeeping for the backoff in onClose below.
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  // Set when a close lands while the tab is hidden: the reopen waits for the
  // page to come back rather than racing a dead camera and a throttled tab.
  const reconnectWhenVisibleRef = useRef(false);

  // Diagnostics for the "socket dies while the window is minimized" report.
  // Enough to separate a proxy/idle close from a daphne ping timeout, and to say
  // whether the page was hidden — and for how long — when it landed. See
  // backend-requests/2026-08-26-websocket-drops-when-browser-minimized.md.
  const socketOpenedAtRef = useRef(null);
  const hiddenSinceRef = useRef(null);

  useEffect(() => {
    const handleHiddenClock = () => {
      hiddenSinceRef.current = document.visibilityState === 'hidden' ? Date.now() : null;
    };

    document.addEventListener('visibilitychange', handleHiddenClock);

    return () => document.removeEventListener('visibilitychange', handleHiddenClock);
  }, []);

  useEffect(() => {
    // No camera behind this session (offline / prescribed without motion capture)
    // — there is no geometry to wait for.
    if (!enabled || !cameraAvailable || isFrameGeometrySettled) {
      return undefined;
    }

    const hasFrameGeometry = () => {
      const { frameWidth, frameHeight } = resolveFrameGeometry(videoRef);
      return Boolean(frameWidth && frameHeight);
    };

    if (hasFrameGeometry()) {
      setIsFrameGeometrySettled(true);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (hasFrameGeometry()) {
        setIsFrameGeometrySettled(true);
      }
    }, FRAME_GEOMETRY_POLL_MS);

    const timeoutId = window.setTimeout(() => {
      console.warn(
        '[socket-config] camera reported no frame width/height in time; opening the session without it — the backend will use its aspect and pixel defaults.'
      );
      setIsFrameGeometrySettled(true);
    }, FRAME_GEOMETRY_WAIT_MS);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [cameraAvailable, enabled, isFrameGeometrySettled, videoRef]);

  // One place decides when a closed socket is allowed to reopen. `setReconnect`
  // needs a false->true edge for useWebSocket to open a new socket, so the flip
  // back is always deferred — by the backoff delay, or until the page is
  // visible again, whichever is later.
  const scheduleReconnect = useCallback(() => {
    setReconnect(false);

    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // A hidden tab has no camera worth reporting and a throttled timer anyway.
    // Reopening from here is how a backgrounded session used to reconnect with
    // `cameraAvailable: false` and pin itself to NO_MOTION_CAPTURE.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      reconnectWhenVisibleRef.current = true;
      console.warn('[socket-reconnect] deferred: page hidden, waiting for it to come back');
      return;
    }

    const step = Math.min(reconnectAttemptsRef.current, RECONNECT_MAX_BACKOFF_STEPS);
    const delay = Math.min(RECONNECT_MAX_DELAY_MS, RECONNECT_BASE_DELAY_MS * 2 ** step);
    reconnectAttemptsRef.current += 1;

    console.warn(
      `[socket-reconnect] scheduled in ${delay}ms, attempt ${reconnectAttemptsRef.current}`
    );
    recordNetworkMetric('websocket', 'reconnect_scheduled', delay, 'ms', {
      userVisibleImpact: 'Reconnection occurred',
    });

    reconnectTimeoutRef.current = window.setTimeout(() => {
      reconnectTimeoutRef.current = null;
      setReconnect(true);
    }, delay);
  }, [recordNetworkMetric]);

  // The only way to correct a `cameraAvailable` the backend has already been
  // told: the protocol has no re-config message, and the consumer holds the
  // config value for the socket's whole lifetime, so the session's capability
  // can only be restated by opening a new socket and letting `onOpen` send a
  // fresh config.
  //
  // It is not free, which is why nothing calls it speculatively: closing the old
  // socket runs the backend's exit handling (it is not yet superseded — the new
  // socket only claims the session when its config arrives), so the in-flight
  // set is finalized and then dropped as an orphan on connect, replaying that
  // set from its start, and a session-exit webhook goes out. Worth it only to
  // escape a whole session stuck without motion capture.
  const forceReconnect = useCallback(reason => {
    console.warn(`[socket-config] reopening the session socket: ${reason}`);

    // Not a failure, so it does not inherit (or advance) the failure backoff.
    reconnectAttemptsRef.current = 0;
    reconnectWhenVisibleRef.current = false;
    setReconnect(false);

    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
    }

    // If the close also lands in onClose, scheduleReconnect clears this timeout
    // and schedules its own — either path reopens exactly once.
    reconnectTimeoutRef.current = window.setTimeout(() => {
      reconnectTimeoutRef.current = null;
      setReconnect(true);
    }, FORCED_REOPEN_DELAY_MS);
  }, []);

  // The deferred half of the reconnect rule: a close that landed while the tab
  // was hidden reopens as soon as it is foregrounded again.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !reconnectWhenVisibleRef.current) {
        return;
      }

      reconnectWhenVisibleRef.current = false;
      scheduleReconnect();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [scheduleReconnect]);

  useEffect(
    () => () => {
      if (reconnectTimeoutRef.current !== null) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    },
    []
  );

  // SOCKET_URL, never a literal: a hardcoded host here silently pointed every
  // environment — production included — at the staging backend.
  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(
    SOCKET_URL,
    {
      onOpen: () => {
        {
          // Diagnostics only — see network-diagnostics-store. Setup time is
          // the CONNECTING->OPEN span; null if this open wasn't preceded by a
          // captured CONNECTING edge (e.g. very first render race).
          useNetworkDiagnostics.getState().setSessionId(initialConfig?.sessionId ?? null);
          if (connectAttemptStartRef.current !== null) {
            recordNetworkMetric(
              'websocket',
              'connection_setup_time',
              Math.round(performance.now() - connectAttemptStartRef.current),
              'ms'
            );
            connectAttemptStartRef.current = null;
          }
          recordNetworkMetric('websocket', 'connection_success', 1, 'count');

          // A socket that actually opened ends the backoff, and tells the page
          // this is a different socket from the one it last spoke to.
          reconnectAttemptsRef.current = 0;
          reconnectWhenVisibleRef.current = false;
          setSocketOpenCount(count => count + 1);
          socketOpenedAtRef.current = Date.now();
          console.warn('[socket-open]', {
            visibility: document.visibilityState,
            cameraAvailable,
          });
          // Camera geometry for the backend's aspect-ratio-invariant pose
          // matching: the matcher un-squashes MediaPipe's frame-normalised
          // landmarks with width/height, so the same movement scores the same
          // on a 16:9 phone and a 4:3 webcam. Read live here rather than from
          // the gate above, so this is the resolution the source reports at the
          // moment the session starts. Omitted fields make the backend fall back
          // to 16:9 (and to its 1280x720 pixel defaults) — which now only happens
          // when there is no camera, or when it stayed silent past
          // FRAME_GEOMETRY_WAIT_MS. See resolveFrameGeometry for how the
          // dimensions are read off the ref.
          const { frameWidth, frameHeight } = resolveFrameGeometry(videoRef);
          // Voice-companion mode, picked in the prescription builder and carried
          // here purely on the URL query param through /camera-permission and
          // /try-demo (see `voiceCompanionMode` above) — no localStorage involved.
          // Read at every socket open, so a reconnect (or the next exercise in the
          // session) tells the backend the same thing. An invalid or missing param
          // falls back to `live` rather than omitting `voiceCompanionMode` — see
          // the config message below.
          //
          // Only the mode rides here. The session's model goes on the call-open
          // request instead (apis/realtime/api.js) — the model is bound to the call
          // when it is created, so that request is the only place it can be chosen.
          const resolvedVoiceCompanionMode = isVoiceCompanionMode(voiceCompanionMode)
            ? voiceCompanionMode
            : VOICE_COMPANION_MODE.live;
          // session_token, same URL relay as voiceCompanionMode above — read
          // fresh on every open rather than captured once, for the same
          // reconnect reason.
          const sessionToken = getSessionTokenFromUrl();
          // No `exerciseId`: the backend reads the report, picks the current
          // exercise itself and builds the evaluator from that exercise's own
          // `execution_mode`.
          //
          // `cameraAvailable` is the one thing the backend cannot know — whether a
          // live media source is really behind the prescribed mode. It is sent
          // once, with this message: there is no re-config message in the
          // protocol, so a camera that comes up later does not update the server
          // (the client's own display reacts locally, and `execution_mode` on each
          // frame stays authoritative). Nothing here depends on the value being
          // echoed back.
          //
          // The same dimensions go out twice under two names because the backend
          // uses them for two different things: `cameraWidth`/`cameraHeight` are
          // reduced to a ratio for the aspect correction, while
          // `frameWidth`/`frameHeight` are the pixel values the static-joint
          // excursion thresholds are judged in. Frame geometry also rides on
          // every landmark frame (see use-predict-worker), so a camera that
          // settles on its resolution after this message still gets reported.
          sendJsonMessage({
            config: {
              sessionId: initialConfig.sessionId,
              cameraAvailable: cameraAvailableRef.current,
              cameraWidth: frameWidth,
              cameraHeight: frameHeight,
              frameWidth,
              frameHeight,
              voiceCompanionMode: resolvedVoiceCompanionMode,
              sessionToken,
              // Which body model the landmarks come from; the backend honours it only where
              // its FASTSAM3D_ENABLED flag is on and falls back to MediaPipe otherwise.
              ...(POSE_BACKEND === 'remote'
                ? {
                    perceptionModel: 'fastsam3d',
                    ...(MHR_FEATURE ? { mhrFeature: MHR_FEATURE } : {}),
                  }
                : {}),
            },
          });
          // The backend re-reads the cached call id when this socket hydrates,
          // so a reopened socket has to be told which call is actually live —
          // otherwise it can keep addressing a call the browser has replaced.
          // No-op unless a call is already established.
          reRegisterRealtimeCall();
          addTimer({ key: 'eft-time', value: time });
          // The wall clock, anchored to this socket and sent as `wallEft`. NOT a
          // mirror of eft-time: it keeps running through the backend's
          // `holdTimerPaused` freeze (see SESSION_WALL_TIMER_KEY in ../helpers).
          addTimer({ key: SESSION_WALL_TIMER_KEY, value: time });
        }
      },
      onError: e => {
        recordNetworkMetric('websocket', 'connection_error', 1, 'count');
        console.error('WebSocket error:', e);
      },
      onMessage: event => {
        // Diagnostics only — the actual message is parsed separately by
        // react-use-websocket into lastJsonMessage; this passive listener
        // just measures the raw frame this hook never otherwise sees the size
        // of. `.length` on a string payload is a close approximation of byte
        // size for the mostly-ASCII JSON this socket carries, not an exact
        // UTF-8 byte count.
        if (typeof event?.data === 'string') {
          recordNetworkMetric('websocket', 'inbound_message_bytes', event.data.length, 'bytes');
          onSocketMessage(event.data);
        }
      },
      onClose: event => {
        const closedAt = Date.now();
        const openedAt = socketOpenedAtRef.current;
        const hiddenSince = hiddenSinceRef.current;
        recordNetworkMetric('websocket', 'connection_closed', event.code, 'code', {
          userVisibleImpact: 'WebSocket disconnected',
        });
        // code 1006 => an unclean cut (proxy or network); 1011/1001 => the server
        // ended it, which for daphne means its ping went unanswered. `hiddenForSec`
        // near 300 points at the browser freezing a hidden page.
        console.warn('[socket-close]', {
          code: event.code,
          reason: event.reason || null,
          wasClean: event.wasClean,
          visibility: document.visibilityState,
          socketAgeSec: openedAt ? Math.round((closedAt - openedAt) / 1000) : null,
          hiddenForSec: hiddenSince ? Math.round((closedAt - hiddenSince) / 1000) : null,
        });
        socketOpenedAtRef.current = null;

        deleteTimer({ key: 'eft-time' });
        // Both clocks are per-socket: the reopened socket re-anchors them at 0.
        deleteTimer({ key: SESSION_WALL_TIMER_KEY });

        // 4401 (expired/invalid session) is the one close code the client
        // cannot recover from by reconnecting — the backend will just reject
        // the new socket the same way. Log the user out instead.
        if (event.code === 4401) {
          removeFromLocalStorage();
          removeFromLocalStorageInPerformance();
          navigate('/login', { replace: true });
          cleanupStream({ videRef: videoRef });
          return;
        }

        // The session is over — nothing left to reconnect to. See the latch
        // above: the backend closes this socket on purpose after stage 5.
        if (isSessionCompleteRef.current) {
          return;
        }

        // Every other close — network drop, an idle proxy/load-balancer
        // timeout while the session sits paused (a paused session sends no
        // frames at all), server restart, anything else — reopens silently,
        // with no modal and no dependence on the user to resume it. Not
        // immediately, though: see scheduleReconnect for the backoff, and for
        // why a close that lands on a hidden tab waits until the page is
        // visible again.
        scheduleReconnect();
      },
    },
    // Don't open the socket (or send the initial config) until the caller
    // says it's ready — e.g. the pose model/worker has finished loading —
    // otherwise the backend session starts before we can actually track. The
    // geometry gate is the second half of the same rule: with a camera behind
    // the session, wait until its width/height can ride on the config message.
    reConnect && enabled && !isSessionComplete && (!cameraAvailable || isFrameGeometrySettled)
  );

  // --- Diagnostics (observation only, see network-diagnostics-store) -------
  // react-use-websocket exposes no "connect attempt started" callback, only
  // readyState — so the CONNECTING edge is the only place to anchor a setup-
  // time measurement. Mirrors the existing `cameraAvailableRef.current = …`
  // pattern above: a plain ref write during render, not inside an effect.
  const connectAttemptStartRef = useRef(null);
  const prevReadyStateRef = useRef(readyState);
  if (readyState !== prevReadyStateRef.current) {
    if (readyState === ReadyState.CONNECTING) {
      connectAttemptStartRef.current = performance.now();
    }
    prevReadyStateRef.current = readyState;
  }

  const connectionStatus = useMemo(
    () =>
      ({
        [ReadyState.CONNECTING]: 'Connecting',
        [ReadyState.OPEN]: 'Open',
        [ReadyState.CLOSING]: 'Closing',
        [ReadyState.CLOSED]: 'Closed',
        [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
      })[readyState],
    [readyState]
  );

  const responseData = useMemo(() => lastJsonMessage && lastJsonMessage['data'], [lastJsonMessage]);

  // A timed hold's credit (conditionsState.hold.credit_s, cumulative in the set): the seconds the
  // patient held before the matcher was sure, which the page adds to its clocks.
  const holdCreditSeconds = useMemo(() => {
    const value = lastJsonMessage?.data?.conditionsState?.hold?.credit_s;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }, [lastJsonMessage]);

  // The TCSA matcher's readout, sent only by a backend running with TCSA_DEBUG=1 (development).
  const tcsaDebug = useMemo(() => lastJsonMessage?.tcsaDebug ?? null, [lastJsonMessage]);

  // The backend sets this on the frame where it fires a voice event at the
  // agent. The page uses it to start the "how long until the user actually
  // hears something" measurement. Optional — the voice still works without it,
  // there's just no latency figure.
  const triggerDispatched = useMemo(
    () => lastJsonMessage && lastJsonMessage['trigger_dispatched'],
    [lastJsonMessage]
  );

  // Sticky stage: not every backend message carries `stage` (landmark/counter/
  // audio-only frames omit it). Reading `responseData?.stage` directly makes it
  // momentarily `undefined` on those frames, which flips every per-stage boolean
  // below to false for a frame — e.g. the "coming up" (EXERCISES_DONE) overlay
  // vanishes and flashes the exercise UI even though the stage never actually
  // changed. Latch the last real numeric stage and only move off it when a
  // message reports a genuinely different stage, so the UI tracks true stage
  // transitions and not the gaps between them.
  const lastValidStageRef = useRef(undefined);
  const stage = useMemo(() => {
    const incoming = responseData?.stage;
    if (incoming !== undefined && incoming !== null) {
      lastValidStageRef.current = incoming;
      return incoming;
    }
    return lastValidStageRef.current;
  }, [responseData]);

  // Development-only network meter (no-op unless enabled): per-stage traffic.
  useEffect(() => {
    reportNetworkMeterStage(stage);
  }, [stage]);

  const isRestStage = useMemo(() => stage === STAGE_STATUS.REST, [stage]);

  const isIntroStage = useMemo(() => stage === STAGE_STATUS.INTRO, [stage]);

  const IntroResult = useMemo(
    () => responseData?.introResult && responseData?.introResult,
    [responseData?.introResult]
  );

  const lastExerciseSummaryRef = useRef(null);
  const exerciseSummary = useMemo(() => {
    if (responseData?.exerciseSummary) {
      lastExerciseSummaryRef.current = responseData.exerciseSummary;
    }

    if (stage === STAGE_STATUS.INTRO || stage === STAGE_STATUS.IN_PERFORMANCE) {
      lastExerciseSummaryRef.current = null;
    }

    return lastExerciseSummaryRef.current;
  }, [responseData?.exerciseSummary, stage]);

  // For the pod's set videos: the set in flight, and the last closed set's record. Latched, since not
  // every frame carries them, and kept by identity so a frame's new copy does not resend it. A record
  // lives from its set's end to the next set or intro (a restart's set 1 is not the old set 1).
  const currentSetRef = useRef(null);
  const currentSet = useMemo(() => {
    if (typeof responseData?.currentSet === 'number') {
      currentSetRef.current = responseData.currentSet;
    }
    return currentSetRef.current;
  }, [responseData?.currentSet]);
  const setRecordRef = useRef(null);
  const setRecord = useMemo(() => {
    const incoming = responseData?.setRecord;
    const last = setRecordRef.current;
    if (stage === STAGE_STATUS.IN_PERFORMANCE || stage === STAGE_STATUS.INTRO) {
      setRecordRef.current = null;
    } else if (incoming && (!last || incoming.order !== last.order || incoming.set !== last.set)) {
      setRecordRef.current = incoming;
    }
    return setRecordRef.current;
  }, [responseData?.setRecord, stage]);

  const isPaseStage = useMemo(
    () => resolvePauseStageContainer(lastJsonMessage)?.pauseStage,
    [lastJsonMessage]
  );

  // Sticky pause payload — same problem the `stage` latch above solves. The
  // backend sends the exercise list once, in the reply to the pause command;
  // every other message (replies to frames that were already in flight when
  // the user hit pause, audio/counter frames) omits `pauseStage` entirely, and
  // reading it straight off the latest message wiped the list to `undefined` a
  // frame later — which is why the pause modal usually showed "No exercises
  // available" even though the data had arrived.
  //
  // Only a message carrying the key as an ARRAY changes the latched value. The
  // backend relays `pauseStage` on EVERY frame, null when unset
  // (base_pose_evaluator._RELAYED_FRAME_KEYS), so clearing on any message that
  // merely *has* the key meant the first frame after the pause reply wiped the
  // list again. That is what emptied the modal on a mid-pause reconnect: the
  // reply to the new socket's own config message carries `pauseStage: null`.
  // A frame that says nothing about the pause list must not clear it; an
  // explicit empty array still does.
  const lastPauseStagePayloadRef = useRef(null);
  // Which exercise (by its pauseStage `order`) the backend currently
  // considers active — a sibling field on the same message, NOT a field on
  // the pauseStage items themselves. It's the only signal available before an
  // exercise actually starts: every item still reports "not_started" (or
  // "skipped", for one already passed) during its own INTRO, so `status`
  // alone can't tell the pause modal which exercise is coming up. Latched the
  // same way as pauseStage itself, for the same reason (see above).
  const lastPauseStageExerciseOrderRef = useRef(null);
  const pauseStageData = useMemo(() => {
    const container = resolvePauseStageContainer(lastJsonMessage);

    if (container) {
      const incoming = container.pauseStage;
      // `status` comes back as a token ("started"), while the pause UI compares
      // against numeric codes — normalize once here so every consumer (card,
      // chip, current-exercise lookup, completed counter) agrees.
      if (Array.isArray(incoming)) {
        lastPauseStagePayloadRef.current =
          incoming.length > 0 ? normalizePauseStageItems(incoming) : null;
      }

      if (container.exercise_order !== undefined) {
        lastPauseStageExerciseOrderRef.current = container.exercise_order;
      }
    }

    return lastPauseStagePayloadRef.current;
  }, [lastJsonMessage]);
  // Plain ref read, not memoized: the memo above already re-runs (and updates
  // this ref) on every lastJsonMessage change, in the same render, before this
  // line executes — nothing here needs its own recomputation to stay in sync.
  const currentPauseStageOrder = lastPauseStageExerciseOrderRef.current;

  // Hold telemetry (motion_hold exercises): the backend reports a block with
  // `hold`, `completion`, `accuracy` and `diagnostics` sub-objects. Its depth in
  // the message isn't fixed — it has shown up on the root, under `data`, and
  // under `response_data` — so walk the message and take the first object that
  // carries a `hold` object instead of betting on one path. The walk is bounded
  // (small depth, arrays skipped past the first few entries) so it stays cheap
  // enough to run on every frame.
  //
  // Latched like `stage` and `pauseStage` above: landmark/audio-only frames omit
  // it entirely, and reading it straight off the latest message made the overlay
  // blink out between real updates.
  const lastHoldTelemetryRef = useRef(null);
  const holdTelemetry = useMemo(() => {
    const match = findHoldTelemetry(lastJsonMessage);

    if (match) {
      if (!lastHoldTelemetryRef.current) {
        // One line on the first hit, so "the overlay isn't showing" can be told
        // apart from "the backend never sent a hold block" without a breakpoint.
        console.warn('[hold-telemetry] first hold block received:', match);
      }
      lastHoldTelemetryRef.current = match;
    }

    return lastHoldTelemetryRef.current;
  }, [lastJsonMessage]);

  // Backend-driven timer gate: the exercise clock (the countdown the user sees)
  // and effective time run only while both `holdTimerPaused` sources on the
  // latest frame report true, and stop otherwise — continuing, not restarting,
  // when they line up again. See resolveHoldTimerRunning for the rule, including
  // why there is no latch across frames that omit the keys.
  const isHoldTimerRunning = useMemo(
    () => resolveHoldTimerRunning(lastJsonMessage),
    [lastJsonMessage]
  );
  const isTimedRepsTimerRunning = useMemo(
    () => resolveTimedRepsTimerRunning(lastJsonMessage),
    [lastJsonMessage]
  );

  // The mode actually in effect for the current exercise, as reported on every
  // frame. Replaces the old `camera_enabled` boolean, and the pause modal's
  // toggle reflects it so it shows what the server is tracking, not a local
  // guess.
  const socketExecutionMode = useMemo(
    () => responseData?.execution_mode,
    [responseData?.execution_mode]
  );

  const latestLatency = useMemo(() => {
    const sentTime =
      lastJsonMessage?.sentAt ??
      lastJsonMessage?.time ??
      lastJsonMessage?.data?.sentAt ??
      lastJsonMessage?.data?.time ??
      lastJsonMessage?.response_data?.sentAt ??
      lastJsonMessage?.response_data?.time;

    if (typeof sentTime !== 'number') {
      return null;
    }

    const latency = performance.now() - sentTime;

    if (Number.isNaN(latency) || latency < 0) {
      return null;
    }

    return Math.round(latency);
  }, [lastJsonMessage]);

  const showUpComingRef = useRef(false);
  const isUpComingStage = useMemo(() => {
    if (!reConnect) {
      showUpComingRef.current = false;
    } else if (stage === STAGE_STATUS.EXERCISES_DONE) {
      showUpComingRef.current = true;
    } else if (stage === STAGE_STATUS.INTRO || stage === STAGE_STATUS.IN_PERFORMANCE) {
      showUpComingRef.current = false;
    }

    return showUpComingRef.current;
  }, [reConnect, stage]);

  const upComingStageResult = useMemo(
    () => (!reConnect ? false : responseData?.upcomingStage),
    [responseData, responseData?.upcomingStage]
  );

  const isTransitionStage = useMemo(
    () => (!reConnect ? false : stage === STAGE_STATUS.INTRO),
    [reConnect, stage]
  );

  const isPostStage = useMemo(
    () => (!reConnect ? false : stage === STAGE_STATUS.POST_PERFORMANCE),
    [reConnect, stage]
  );

  // Latched on the stage itself rather than on `isPostStage`, which is gated by
  // `reConnect` and would miss the flip the reconnect dance causes. The ref is
  // written first so a close arriving before this render commits already sees
  // the session as finished.
  useEffect(() => {
    if (stage !== STAGE_STATUS.POST_PERFORMANCE || isSessionCompleteRef.current) {
      return;
    }

    isSessionCompleteRef.current = true;
    setIsSessionComplete(true);
  }, [stage]);

  const responseLandMarks = useMemo(() => responseData?.landmarks, [responseData]);

  const inPerformance = useMemo(
    () => (!reConnect ? false : stage === STAGE_STATUS.IN_PERFORMANCE),
    [reConnect, stage]
  );

  const setResult = useMemo(
    () => (!reConnect ? false : responseData?.setResult),
    [reConnect, responseData?.setResult]
  );

  // const inPerformance = true;

  const movementType = useMemo(() => responseData?.movement_type, [responseData]);

  const counter = useMemo(() => responseData?.counter, [responseData]);
  const currentScore = useMemo(() => {
    const rawScore =
      responseData?.score ??
      responseData?.currentScore ??
      responseData?.current_score ??
      responseData?.accuracy;

    if (rawScore === undefined || rawScore === null || rawScore === '') {
      return null;
    }

    const numericScore = Number(rawScore);
    if (Number.isNaN(numericScore)) {
      return null;
    }

    return Math.max(0, Math.min(100, Math.round(numericScore)));
  }, [responseData]);

  const currentFps = useMemo(() => {
    const rawFps = lastJsonMessage?.fps;

    if (rawFps === undefined || rawFps === null || rawFps === '') {
      return null;
    }

    const numericFps = Number(rawFps);
    if (Number.isNaN(numericFps)) {
      return null;
    }

    return Math.max(0, Math.round(numericFps));
  }, [lastJsonMessage, responseData]);

  const RectangleColor = !reConnect ? false : responseData?.rectangleColor;

  const isFalseStage = useMemo(() => RectangleColor === rectangleFlag.red, [RectangleColor]);

  const voiceResult = useMemo(
    () => responseData?.audio_data && responseData?.audio_data,
    [responseData]
  );

  const handlelReConnect = () => {
    setReconnect(true);
  };

  const shapeColor = () => {
    return RectangleColor;
  };

  // Diagnostics only — reuses the latency this hook already computes above
  // (client_now - server-tagged sentAt) rather than adding a new correlation
  // mechanism. Not a strict round-trip time: see network-diagnostics-store
  // consumers / the Step 2 write-up for that caveat.
  useEffect(() => {
    if (latestLatency !== null) {
      recordNetworkMetric('websocket', 'message_latency', latestLatency, 'ms');
    }
  }, [latestLatency, recordNetworkMetric]);

  useEffect(() => {
    if (latestLatency === null) {
      return undefined;
    }

    const now = performance.now();
    const samples = latencySamplesRef.current.filter(
      sample => now - sample.recordedAt <= LATENCY_WINDOW_MS
    );

    samples.push({
      value: latestLatency,
      recordedAt: now,
    });

    latencySamplesRef.current = samples;
    const averageLatency = Math.round(
      samples.reduce((total, sample) => total + sample.value, 0) / samples.length
    );
    setCurrentLatency(averageLatency);

    return undefined;
  }, [latestLatency]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const now = performance.now();
      const samples = latencySamplesRef.current.filter(
        sample => now - sample.recordedAt <= LATENCY_WINDOW_MS
      );

      latencySamplesRef.current = samples;

      if (samples.length === 0) {
        setCurrentLatency(null);
        return;
      }

      const averageLatency = Math.round(
        samples.reduce((total, sample) => total + sample.value, 0) / samples.length
      );
      setCurrentLatency(averageLatency);
    }, LATENCY_WINDOW_MS);

    return () => {
      window.clearInterval(intervalId);
      latencySamplesRef.current = [];
    };
  }, []);

  return {
    connectionStatus,
    data: responseData?.data,
    responseLandMarks,
    inPerformance,
    sendJsonMessage,
    lastJsonMessage,
    tcsaDebug,
    holdCreditSeconds,
    shapeColor,
    isRestStage,
    handlelReConnect,
    isPostStage,
    voiceResult,
    setReconnect,
    movementType,
    counter,
    currentScore,
    currentFps,
    currentLatency,
    triggerDispatched,
    isFalseStage,
    isUpComingStage,
    isTransitionStage,
    setResult,
    upComingStageResult,
    isPaseStage,
    pauseStageData,
    currentPauseStageOrder,
    socketExecutionMode,
    holdTelemetry,
    isHoldTimerRunning,
    isTimedRepsTimerRunning,
    reConnect,
    IntroResult,
    exerciseSummary,
    isIntroStage,
    stage,
    socketOpenCount,
    forceReconnect,
    currentSet,
    setRecord,
  };
};

export default UseWebSocketConnection;
